import { Field, Form, Formik } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationMessage from '@/common/components/blocks/Modal/Common/ConfirmationMessage/ConfirmationMessage';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import SelectField from '@/common/components/molecules/SelectField/SelectField';
import TextField from '@/common/components/molecules/TextField/TextField';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import { useTagManager } from '@/common/contexts/TagManagerContext';
import useCountries from '@/common/hooks/countries/useCountries';
import useManageWorksite from '@/common/hooks/worksites/useManageWorksite';
import { TAG_MANAGER } from '@/ebat/constants';

import {
  WorksiteFormDefaultProps,
  WorksiteFormPropTypes,
} from './WorksiteForm.proptypes';

const WorksiteForm = ({
  address,
  code,
  disabled,
  isLoading,
  label,
  onSuccess,
  refetch,
  setShowForm,
}) => {
  const { t } = useTranslation();
  const { user, zergRush } = useAuth();
  const {
    onCloseModal,
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
  } = useModalContext();
  const { onPushEvent } = useTagManager();

  const {
    isDisabled: isDisabledUseManageWorksite,
    onCreateWorksite,
    onDeleteWorksite,
    onUpdateWorksite,
  } = useManageWorksite({ disabled, isLoading });

  const { data: countries, isLoadingCountries } = useCountries({
    shippingAvailable: 1,
  });

  /**
   * Return the countries list available for delivery
   * @returns {{}}
   */
  const countriesList = useMemo(
    () =>
      countries?.data?.map(({ id: value, label: key }) => ({
        key,
        value: value.toString(),
      })),
    [countries],
  );

  const cancelWorksite = () => {
    setShowForm(false);
    onCloseModal();
  };

  const handleOpenModal = useCallback(
    (type) => {
      switch (type) {
        case 'RemoveWorksite':
          setModalContent(
            <ConfirmationMessage
              description={t(
                'common:messages:actions.worksite.delete.description',
              )}
              onSubmit={() => onDeleteWorksite({ code, onSuccess: refetch })}
              title={t('common:messages:actions.worksite.delete.title')}
            />,
          );
          setModalClassName('modal-confirmation-message');
          setModalSize('sm');
          break;
        default:
          return null;
      }

      return onOpenModal();
    },
    [
      code,
      onDeleteWorksite,
      onOpenModal,
      refetch,
      setModalClassName,
      setModalContent,
      setModalSize,
      t,
    ],
  );

  const isDisabled = useMemo(
    () => ({
      loading:
        isLoading || isLoadingCountries || isDisabledUseManageWorksite.loading,
      manage:
        disabled ||
        isDisabledUseManageWorksite.disabled ||
        isLoadingCountries ||
        isLoading ||
        zergRush ||
        !user?.functionality_accesses?.worksites ||
        !user?.functionality_accesses?.worksites_manage,
    }),
    [
      disabled,
      isDisabledUseManageWorksite,
      isLoading,
      isLoadingCountries,
      user,
      zergRush,
    ],
  );

  /**
   * Check if all field are filled
   * @param {object} worksite
   */
  const isSubmitDisabled = useCallback(
    (worksite) => {
      const { address: worksiteAddress, label: worksiteLabel } = worksite;
      return (
        !worksiteLabel ||
        !worksiteAddress.name ||
        !worksiteAddress.street1 ||
        !worksiteAddress.postcode ||
        !worksiteAddress.city ||
        isDisabled.manage
      );
    },
    [isDisabled],
  );

  /**
   * Set initial values for Formik
   * @type {object}
   */
  const initialValues = useMemo(
    () => ({
      address: {
        city: address?.city || '',
        country: address?.country || 'FR',
        fax: address?.fax || '',
        name: address?.name || '',
        phone1: address?.phone1 || '',
        phone2: address?.phone2 || '',
        postcode: address?.postcode || '',
        street1: address?.street1 || '',
        street2: address?.street2 || '',
        street3: address?.street3 || '',
      },
      code: code || '',
      label: label || '',
    }),
    [address, code, label],
  );

  /**
   * Push dataLayer
   * @type {function(): void}
   */
  const handlePushDataLayer = useCallback(
    () =>
      onPushEvent({
        event: TAG_MANAGER.EVENTS.CTA_ADD_WORKSITE,
      }),
    [onPushEvent],
  );

  /**
   * Actions on add worksite success
   * @type {(...args: any[]) => any}
   * @param newCode {number} : code of new worksite
   */
  const handleAddWorksiteSuccess = useCallback(
    (newCode) => {
      refetch();
      setShowForm(false);
      onSuccess(newCode);
      handlePushDataLayer();
    },
    [handlePushDataLayer, onSuccess, refetch, setShowForm],
  );

  /**
   * Action on form submit : create or update worksite
   * @type {(function(*, *): void)|*}
   */
  const handleSubmit = useCallback(
    (values, actions) => {
      if (code) {
        onUpdateWorksite({
          values,
          actions,
          onSuccess: refetch,
        });
      } else {
        onCreateWorksite({
          values,
          actions,
          onSuccess: (data) => handleAddWorksiteSuccess(data.code),
        });
      }
    },
    [
      code,
      handleAddWorksiteSuccess,
      onCreateWorksite,
      onUpdateWorksite,
      refetch,
    ],
  );

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => handleSubmit(values, actions)}
      >
        {({ errors, touched, values }) => (
          <Form className="form worksites__form" data-qa-id="worksites-form">
            <Field
              as={TextField}
              context="code-field"
              disabled={!!code}
              errors={
                errors.code &&
                touched.code && [
                  t(`common:messages:forms.validation.${errors.code}`),
                ]
              }
              id="code"
              maxLength={6}
              name="code"
            >
              {t('common:messages:forms.fields.code')}
            </Field>
            <Field
              as={TextField}
              context="label-field"
              disabled={isDisabled.manage}
              errors={
                errors.label &&
                touched.label && [
                  t(`common:messages:forms.validation.${errors.label}`),
                ]
              }
              id="label"
              maxLength={50}
              name="label"
              required
            >
              {t('common:messages:forms.worksite.fields.label')}
            </Field>
            <Field
              as={TextField}
              context="address-name-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[name]'] &&
                touched.address?.name && [
                  t(
                    `common:messages:forms.validation.${errors['address[name]']}`,
                  ),
                ]
              }
              id="address.name"
              maxLength={50}
              name="address.name"
              required
            >
              {t('common:messages:forms.fields.name')}
            </Field>
            <Field
              as={TextField}
              context="address-street1-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[street1]'] &&
                touched.address?.street1 && [
                  t(
                    `common:messages:forms.validation.${errors['address[street1]']}`,
                  ),
                ]
              }
              id="address.street1"
              maxLength={30}
              name="address.street1"
              required
            >
              {t('common:messages:forms.fields.street1')}
            </Field>
            <Field
              as={TextField}
              context="address-street2-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[street2]'] &&
                touched.address?.street2 && [
                  t(
                    `common:messages:forms.validation.${errors['address[street2]']}`,
                  ),
                ]
              }
              id="address.street2"
              maxLength={30}
              name="address.street2"
            >
              {t('common:messages:forms.fields.street2')}
            </Field>
            <Field
              as={TextField}
              context="address-street3-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[street3]'] &&
                touched.address?.street3 && [
                  t(
                    `common:messages:forms.validation.${errors['address[street3]']}`,
                  ),
                ]
              }
              id="address.street3"
              maxLength={30}
              name="address.street3"
            >
              {t('common:messages:forms.fields.street3')}
            </Field>
            <Field
              as={TextField}
              context="address-postcode-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[postcode]'] &&
                touched.address?.postcode && [
                  t(
                    `common:messages:forms.validation.${errors['address[postcode]']}`,
                  ),
                ]
              }
              id="address.postcode"
              maxLength={5}
              name="address.postcode"
              required
            >
              {t('common:messages:forms.fields.postcode')}
            </Field>
            <Field
              as={TextField}
              context="address-city-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[city]'] &&
                touched.address?.city && [
                  t(
                    `common:messages:forms.validation.${errors['address[city]']}`,
                  ),
                ]
              }
              id="address.city"
              maxLength={30}
              name="address.city"
              required
            >
              {t('common:messages:forms.fields.city')}
            </Field>
            <Field
              as={SelectField}
              context="address-country-field"
              disabled={isDisabled.manage || !countriesList}
              errors={
                errors['address[country]'] &&
                touched.address?.country && [
                  t(
                    `common:messages:forms.validation.${errors['address[country]']}`,
                  ),
                ]
              }
              id="address.country"
              name="address.country"
              options={countriesList || []}
            >
              {t('common:messages:forms.fields.country')}
            </Field>
            <Field
              as={TextField}
              context="address-phone1-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[phone1]'] &&
                touched.address?.phone1 && [
                  t(
                    `common:messages:forms.validation.${errors['address[phone1]']}`,
                  ),
                ]
              }
              id="address.phone1"
              maxLength={20}
              name="address.phone1"
            >
              {t('common:messages:forms.fields.phone1')}
            </Field>
            <Field
              as={TextField}
              context="address-phone2-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[phone2]'] &&
                touched.address?.phone2 && [
                  t(
                    `common:messages:forms.validation.${errors['address[phone2]']}`,
                  ),
                ]
              }
              id="address.phone2"
              maxLength={20}
              name="address.phone2"
            >
              {t('common:messages:forms.fields.phone2')}
            </Field>
            <Field
              as={TextField}
              context="address-fax-field"
              disabled={isDisabled.manage}
              errors={
                errors['address[fax]'] &&
                touched.address?.fax && [
                  t(
                    `common:messages:forms.validation.${errors['address[fax]']}`,
                  ),
                ]
              }
              id="address.fax"
              maxLength={20}
              name="address.fax"
            >
              {t('common:messages:forms.fields.fax')}
            </Field>
            <ButtonLoading
              context="worksite-save"
              disabled={isSubmitDisabled(values)}
              isLoading={isDisabled.loading}
            >
              {code
                ? t('common:messages:actions.save')
                : t('common:messages:actions.create_item', {
                    item: t('common:messages:types.the_worksite'),
                  })}
            </ButtonLoading>
            <ButtonLoading
              context="worksite-cancel"
              disabled={isDisabled.manage}
              isLoading={isDisabled.loading}
              onClick={() =>
                code ? handleOpenModal('RemoveWorksite') : cancelWorksite()
              }
              type="button"
              variant="secondary-light"
            >
              {code
                ? t('common:messages:actions.delete_item', {
                    item: t('common:messages:types.the_worksite'),
                  })
                : t('common:messages:actions.cancel')}
            </ButtonLoading>
          </Form>
        )}
      </Formik>
      <p className="label">{t('common:messages:forms.required')}</p>
    </>
  );
};

WorksiteForm.defaultProps = {
  ...WorksiteFormDefaultProps,
};

WorksiteForm.propTypes = {
  ...WorksiteFormPropTypes,
};

export default WorksiteForm;
